import { BlobApi } from '../../api/blob-api';
import { ClientApi } from '../../api/client-api';
import { customElement } from 'lit/decorators.js';
import { DataCacheGeneric } from '../../../webmodule-common/cache/generic-data-cache';
import { emptyGuid } from '../../../webmodule-common/other/api/guid';
import { EventGetReference, EventSnippet, EventTemplate, NullPromise } from '../../../webmodule-common/other/ui/events';
import { getApiFactory } from '../../api/api-injector';
import { getInternalId } from '../../../webmodule-common/other/ui/databinding/databinding';
import { html } from 'lit';
import { IconRefresh } from '../../../webmodule-common/other/ui/icons/icon-refresh';
import { IStaleData } from '../../../webmodule-common/other/stale-data';
import { launchQuote, resourceQuote } from '../ui/launcher';
import { LitTableWrapper } from '../../../webmodule-common/other/ui/littable-view';
import { moneyToHtml } from '../../../webmodule-common/other/currency-formatter';
import { PageControl, PageControlOptions, PageManager } from '../../../webmodule-common/other/ui/page-control';
import { QuoteApi } from '../../api/quote-api';
import { QuoteContainer, QuoteContainerManager } from '../data/quote-container';
import { QuoteState, ResultGetQuoteSummary, ViewQuoteSummary } from '../../api/dealer-api-interface-quote';
import { QuoteView, QuoteViewOptions } from './quote-view';
import { RequestPage, ResultPaginated } from '../../../webmodule-common/other/ui/RequestPage';
import { resolveURL } from '../../../webmodule-common/other/ui/resource-resolver';
import { resourceClient } from '../../dealer-franchisee/clients/ui/launcher';
import { LangQuoteListView } from '../../../localization/scripts/quotes/views/quote-list-view-lang';
import { ViewBase } from '../../../webmodule-common/other/ui/view-base';
import {
  WebModuleLitTable,
  WebModuleLitTableColumnDef
} from '../../../webmodule-common/components/src/webmodule-components';

export type TableEventQuote = (quoteSummary: ViewQuoteSummary) => Promise<void>;

export type EventLink = (linkId: string) => Promise<void>;

export interface QuoteSummaryTableOptions {
  quoteOwnerId: EventGetReference;
  numberOfDaysHistory?: number;
  quoteStates?: QuoteState;
  customerCache: DataCacheGeneric;
  userProfileCache: DataCacheGeneric;
  title: EventSnippet;
  copyQuoteEvent: TableEventQuote;
  deleteQuoteEvent: TableEventQuote;
  pageFragment: string;
  stale?: IStaleData;
}

//Basic class for displaying quotes in a table form
@customElement('wm-quotesummarytable')
export class QuoteSummaryTable extends LitTableWrapper<ViewQuoteSummary> {
  eventTitle: EventSnippet;
  api: QuoteApi = getApiFactory().quote();
  quoteOwnerId: EventGetReference;
  quoteStates: QuoteState;
  customerCache: DataCacheGeneric;
  userCache: DataCacheGeneric;
  copyQuoteEvent: TableEventQuote;
  deleteQuoteEvent: TableEventQuote;

  private titleFilter: string | null;
  protected assignedToUserId: string | null = null;
  numberOfDaysHistory: number | undefined;
  pageFragment: string;
  dataIsStale = false;

  constructor(options: QuoteSummaryTableOptions) {
    super();
    this.eventTitle = options.title;
    this.quoteOwnerId = options.quoteOwnerId;
    this.quoteStates = options.quoteStates ?? QuoteState.None;
    this.customerCache = options.customerCache;
    this.userCache = options.userProfileCache;
    this.copyQuoteEvent = options.copyQuoteEvent;
    this.deleteQuoteEvent = options.deleteQuoteEvent;
    this.numberOfDaysHistory = options.numberOfDaysHistory;
    this.pageFragment = options.pageFragment;

    this.titleFilter = null;
  }

  isDataLoadDelayed() {
    return true;
  }

  async getRowsFromServer(request: RequestPage): Promise<ResultPaginated<ViewQuoteSummary>> {
    const results = await this.api.getQuoteSummary({
      numberOfDaysHistory: this.numberOfDaysHistory ?? null,
      quoteOwnerId: await this.quoteOwnerId(),
      pageIndex: request.pageIndex,
      pageSize: request.pageSize,
      statesToLoad: this.quoteStates,
      title: this.titleFilter,
      description: null,
      sortField: request.sortField,
      sortAsc: request.sortAsc,
      assignedToUserId: this.assignedToUserId
    });
    if (!results) {
      return {
        count: 0,
        pageCount: 0,
        pageIndex: 0,
        pageSize: this.pageLength(),
        results: []
      };
    } else {
      //if there are any other callbacks we need to do, add them to the
      //all array which will let them run at the same time
      //pre fetch all linked data via a cache for displaying quickly reference information
      const pf = this.getPreFetched(results);
      await Promise.all(pf);

      return results.quoteSummary;
    }
  }

  async deleteQuoteItem(data: ViewQuoteSummary) {
    await this.deleteQuoteEvent?.(data);
  }

  async copyQuoteItem(data: ViewQuoteSummary) {
    await this.copyQuoteEvent?.(data);
  }

  getDefaultSortField(): string | undefined {
    return 'quoteNumber';
  }

  getColumns(): WebModuleLitTableColumnDef[] {
    return [
      {
        title: () => LangQuoteListView.quoteNumberTitle(),
        fieldName: 'quoteNumber',
        classes: 'colpx-90',
        displayValue: (_table: WebModuleLitTable, item: unknown) => {
          const quoteSummary = item as ViewQuoteSummary;
          return this.quoteLink(
            quoteSummary,
            quoteSummary.quoteNumber == 0 ? 'N/A' : quoteSummary.quoteNumber.toString()
          );
        }
      },
      {
        title: () => LangQuoteListView.titleTitle(),
        fieldName: 'title',
        classes: 'colpx-200',
        displayValue: (_table: WebModuleLitTable, item: unknown) => {
          const quoteSummary = item as ViewQuoteSummary;
          return this.quoteLink(quoteSummary, quoteSummary.quoteNumber.toString());
        }
      },
      {
        title: () => LangQuoteListView.descriptionTitle(),
        fieldName: 'description',
        classes: 'colpx-200',
        displayValue: (_table: WebModuleLitTable, item: unknown) => {
          const quoteSummary = item as ViewQuoteSummary;
          return this.quoteLink(quoteSummary, quoteSummary.description);
        }
      },
      {
        title: () => LangQuoteListView.clientTitle(),
        fieldName: 'quoteCustomerId',
        classes: 'colpx-150',
        displayValue: (_table: WebModuleLitTable, item: unknown) => {
          const quoteSummary = item as ViewQuoteSummary;
          return this.getCustomerLink(quoteSummary.quoteCustomerId);
        }
      },
      {
        title: () => LangQuoteListView.createdByTitle(),
        fieldName: 'creationUserId',
        classes: 'colpx-150',
        displayValue: (_table: WebModuleLitTable, item: unknown) => {
          const quoteSummary = item as ViewQuoteSummary;
          return this.getUserDisplayValue(quoteSummary.creationUserId);
        }
      },
      {
        title: () => LangQuoteListView.modifiedTitle(),
        fieldName: 'lastModifiedDate',
        classes: 'colpx-150',
        displayValue: (_table: WebModuleLitTable, item: unknown) => {
          const quoteSummary = item as ViewQuoteSummary;
          const dt = new Date(quoteSummary.lastModifiedDate ?? '');
          return `${dt.toLocaleDateString()} ${dt.toLocaleTimeString()}`;
        }
      },
      {
        title: () => LangQuoteListView.grossTotalTitle(),
        fieldName: 'calculatedGrossTotal',
        classes: 'colpx-100 alignment-right',
        displayValue: (_table: WebModuleLitTable, item: unknown) => {
          const quoteSummary = item as ViewQuoteSummary;
          return moneyToHtml(quoteSummary.calculatedGrossTotal);
        }
      },
      {
        title: () => LangQuoteListView.netTotalTitle(),
        fieldName: 'calculatedNetTotal',
        classes: 'colpx-100 alignment-right',
        displayValue: (_table: WebModuleLitTable, item: unknown) => {
          const quoteSummary = item as ViewQuoteSummary;
          return moneyToHtml(quoteSummary.calculatedNetTotal);
        }
      }
    ];
  }

  useAutoWidthColumns(): boolean {
    return true;
  }

  override enableFiltering(): boolean {
    return true;
  }

  override updateFilter(_searchTerm: string | null) {
    this.titleFilter = _searchTerm;
  }

  protected getPreFetched(results: ResultGetQuoteSummary): Promise<void>[] {
    const customerKeys = results.quoteSummary.results?.map(x => x.quoteCustomerId) ?? [];
    const userKeys = results.quoteSummary.results?.map(x => x.assignedToUserId) ?? [];

    return [this.customerCache.preFetch(customerKeys), this.userCache.preFetch(userKeys)];
  }

  protected quoteLink(row: ViewQuoteSummary, value: string) {
    return html`<a class="quote-link" href="${resolveURL(resourceQuote, row.id)}" quoteid="${row.id}">${value}</a>`;
  }

  protected getUserDisplayValue(value: string) {
    if (value == emptyGuid) return '';
    //prefetch should already have been called
    return html`${this.userCache.getLocal(value)?.displayValue}` ?? '';
  }

  protected getCustomerLink(id: string) {
    if (id == emptyGuid) return '';
    //prefetch should already have been called
    const customer = this.customerCache.getLocal(id);
    return customer
      ? html`<a class="client-link" href="${resolveURL(resourceClient, id)}" clientid="${id}"
          >${customer.displayValue}</a
        >`
      : '';
  }
}

export type QuoteViewFactory = (options: QuoteViewOptions) => QuoteView;

export interface QuoteListViewOptions {
  quoteOwnerId: EventGetReference;
  customerCache: DataCacheGeneric;
  userProfileCache: DataCacheGeneric;
}

//TODO.. there is a lot of generic potential in here to create a consistent PAGE control
// i think the page control code should be extrapolated into a page control template render
// that can bind events and content into the pages.
@customElement('wm-quotelistview')
export class QuoteListView extends ViewBase {
  protected tables: QuoteSummaryTable[];
  protected readonly quoteApi: QuoteApi = getApiFactory().quote();
  protected readonly quoteOwnerId: EventGetReference;
  protected readonly elementId: string;
  protected readonly customerCache: DataCacheGeneric;
  protected readonly userProfileCache: DataCacheGeneric;
  protected pageControl: PageControl;
  //propagated to quoteeditor
  protected readonly blobApi: BlobApi = getApiFactory().blob();
  protected readonly clientApi: ClientApi = getApiFactory().client();

  constructor(options: QuoteListViewOptions) {
    super();
    //TODO - maybe we want to inject an API.. but i dont think we need to
    this.elementId = `quote-list-view-${getInternalId()}`;
    this.quoteOwnerId = options.quoteOwnerId;
    this.customerCache = options.customerCache;
    this.userProfileCache = options.userProfileCache;

    this.tables = this.initializeTables();
    this.pageControl = this.createPageControl();
  }

  public async setActiveTabByHash() {
    await this.pageControl?.applyWindowHash();
  }

  userLoggedOff() {
    //force a refresh next time we log-in
    this.tables.forEach(t => (t.lastRefresh = new Date('2000-01-01')));
  }
  protected getInitialPageManagers(): PageManager[] {
    return this.tables.map(table => {
      const pm: PageManager = {
        caption: table.eventTitle,
        canClose: () => Promise.resolve(false),
        canLeave: () => Promise.resolve(true),
        hasDelete: () => false,
        onEnter: async () => {
          //enable data load the first time we enter the tab, but not before
          table.delayedDataLoad = false;
          // not-awaiting on purpose, for faster page switch. safe to do
          if (table.dataIsStale) table.refreshData();
        },
        content: () => {
          return table;
        },
        data: table,
        pageFragment: table.pageFragment
      };
      return pm;
    });
  }
  createPageControl(): PageControl {
    // build static pages for each of the configured table settings

    const options: PageControlOptions = {
      defaultTabIndex: 0,
      menuIcons: [
        {
          event: async () => {
            if (this.pageControl.activeTabIndex < this.tables.length) {
              const table = this.pageControl.activePage?.data as QuoteSummaryTable;
              this.beforeRefresh();
              await table.refreshData();
              this.tables.forEach(t => {
                if (t !== table) {
                  t.dataIsStale = true;
                }
              });
              this.afterRefresh();
              return true;
            } else return false;
          },
          caption: () => html`${new IconRefresh()}`
        }
      ],
      plusPage: {
        caption: () => LangQuoteListView.createQuote(),
        event: async () => await this.addNewQuote()
      },
      pageInitializer: () => this.getInitialPageManagers()
    };
    return new PageControl(options);
  }

  afterRefresh() {
    //nothing
  }

  beforeRefresh() {
    //nothing
  }

  protected async createNewQuote(): NullPromise<QuoteContainer> {
    //TODO-override this at another level
    return null;
  }

  async addNewQuote(): Promise<boolean> {
    const quoteContainer = await this.createNewQuote();
    if (quoteContainer && quoteContainer.quote) {
      return await launchQuote(quoteContainer.quoteId);
    }
    return false;
  }

  private async refreshTabsForState(state: number) {
    const res: Promise<void>[] = [];
    for (let i = 0; i < this.tables.length; i++) {
      const t = this.tables[i];
      if (t.quoteStates && state == state) {
        t.refreshData();
        res.push(t.refreshData());
        this.pageControl.requestUpdate();
      }
    }
    await Promise.allSettled(res);
  }

  private async updateAndRefreshQuoteTab(switchToActiveTab?: boolean) {
    if (switchToActiveTab) {
      if (this.pageControl.activeTabIndex !== 0) {
        this.tables[0].refreshData();
        await this.pageControl.setActiveTabIndex(0);
      } else this.tables[0].refreshData();
    } else {
      this.tables[this.pageControl.activeTabIndex].refreshData();
    }
  }

  async init() {
    //override me
  }

  //this can be overridden in sub class to replace the table with different column/view etc
  protected quoteSummaryTableFactory(options: QuoteSummaryTableOptions): QuoteSummaryTable {
    return new QuoteSummaryTable(options);
  }

  protected createSummaryTable(
    title: EventSnippet,
    quoteStates: QuoteState,
    pageFragment: string,
    numberOfDaysHistory: number | undefined
  ): QuoteSummaryTable {
    return this.quoteSummaryTableFactory({
      quoteOwnerId: this.quoteOwnerId,
      quoteStates: quoteStates,
      numberOfDaysHistory: numberOfDaysHistory,
      customerCache: this.customerCache,
      userProfileCache: this.userProfileCache,
      title: title,
      copyQuoteEvent: async (quoteSummary: ViewQuoteSummary) => {
        await this.copyQuote(quoteSummary);
      },
      deleteQuoteEvent: async (quoteSummary: ViewQuoteSummary) => {
        await this.deleteQuote(quoteSummary);
      },
      pageFragment: pageFragment
    });
  }

  //override this in a subclass to change the design, and order of the tables
  protected initializeTables(): QuoteSummaryTable[] {
    return [
      this.createSummaryTable(() => LangQuoteListView.allQuotes(), QuoteState.None, 'allQuotes', undefined),
      this.createSummaryTable(() => LangQuoteListView.draft(), QuoteState.Draft, 'draft', undefined),
      this.createSummaryTable(
        () => LangQuoteListView.accepted(),
        // eslint-disable-next-line no-bitwise
        QuoteState.Accepted | QuoteState.Accepted_AssignedToReviewer,
        'accepted',
        undefined
      ),
      this.createSummaryTable(() => LangQuoteListView.active(), QuoteState.Active, 'active', undefined),
      // eslint-disable-next-line no-bitwise
      this.createSummaryTable(
        () => LangQuoteListView.issued(),
        QuoteState.Issued | QuoteState.IssuePending,
        'issused',
        undefined
      ),
      this.createSummaryTable(
        () => LangQuoteListView.rejected(),
        // eslint-disable-next-line no-bitwise
        QuoteState.Rejected | QuoteState.Accepted_RefusedBySupplier,
        'rejected',
        30
      )
    ];
  }

  protected createQuoteContainer(quoteId: string): QuoteContainer {
    return new QuoteContainer(quoteId, null, null, null, null, null, null, null, null); //this is a delayed load item
  }

  protected async copyQuote(quoteSummary: ViewQuoteSummary): Promise<void> {
    const qcm = await this.createQuoteContainerManager(this.createQuoteContainer(quoteSummary.id));
    const copy = await qcm.makeCopy();
    if (copy) {
      await this.updateAndRefreshQuoteTab();
      launchQuote(copy.quoteId);
    }
  }

  protected createQuoteContainerManager(quoteContainer: QuoteContainer): QuoteContainerManager {
    return new QuoteContainerManager(quoteContainer, this.quoteApi, this.blobApi);
  }

  protected async deleteQuote(quoteSummary: ViewQuoteSummary): Promise<void> {
    const qcm = await this.createQuoteContainerManager(this.createQuoteContainer(quoteSummary.id));
    const deleted = (await qcm.deleteQuote()) ?? false;

    if (deleted) {
      await this.refreshTabsForState(quoteSummary.state);
      await this.doAfterQuoteEdit();
    }
  }

  doAfterQuoteEdit() {
    //override;
  }

  protected template(): EventTemplate {
    return html` <div id=${this.elementId} class="page-content">${this.pageControl.ui}</div>`;
  }
}
